import {
    OnChanges,
    OnInit,
    OnDestroy,
    SimpleChanges,
    AfterViewInit,
    ElementRef,
    Renderer2,
    Input,
    Output, EventEmitter, ViewChild, HostListener, AfterViewChecked
} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import * as moment from 'moment';
import * as fileSaver from 'file-saver';
import 'rxjs/add/operator/first';

import {BufferGraphService} from 'app/buffer-graph/services/buffer-graph.service';
import {SlickGridComponent} from 'app/slick-grid/slick-grid.component';
import {ISlickGridColumn} from 'app/slick-grid/interfaces/slick-grid-column';
import {ColumnSettingsBase} from "../../slick-grid/models/column-settings-base";
import {TranslateService} from "@ngx-translate/core";
import {ChartHelper} from "app/buffer-graph/models/chart-helper";
import {ISubscription} from "rxjs/Subscription";
import {BufferGraphDataExchangeService} from "../services/buffer-graph-data-exchange.service";
import {ITabInfo} from "./tab-info-interface";
import {ChartData} from "./chart-data";
import {PeriodSelectorModel} from "../../shared/models/period-selector-model";
import {CodeName} from "../../stocks/models/code-name";
import {ColumnsGenerator} from "../../slick-grid/models/columns-generator";
import {OnDropdownFilteredEventArgs} from "../../slick-grid/models/on-dropdown-filtered-event-args";
import {TabInfoBase} from "./tab-info-base";
import {GUID} from 'app/shared/models/GUID-interface';
import {v} from "@angular/core/src/render3";
import {BGChartType} from "./b-g-chart-type";
import {FreshItemActionLine} from "./item-actions/fresh-item-action-line";
import {ExcelWorkService} from "../../services/excel-work.service";
import {ModuleTableName} from "../../shared/models/module-table-name";
import {ExportExcelColumn} from "../../shared/models/export-excel-column";
import {CustomFunctionReturn} from "../../slick-grid/models/custom-function-return";
import {FilterByDateRange} from "../../shared/models/filter-by-date-range";
import {tryCatch} from "rxjs/util/tryCatch";
import {Size} from "../../shared/models/size";

export abstract class TabComponentBase<T extends Slick.SlickData, T2 extends { columns: ExportExcelColumn[] }> implements OnChanges, OnInit, AfterViewInit, OnDestroy, AfterViewChecked{
    protected constructor(protected readonly graphService: BufferGraphService,
                          protected readonly exchangeService: BufferGraphDataExchangeService,
                          protected readonly excelService: ExcelWorkService,
                          protected readonly translateService: TranslateService,
                          protected readonly renderer: Renderer2,
                          protected readonly elementRef: ElementRef
    ){
        this.columns = [];
        this.lineColumns = [];
        this.helper = new ChartHelper();
        // this.updateState = new EventEmitter<string>();
        this.updateData = new EventEmitter<PeriodSelectorModel>();
        this.close = new EventEmitter<ITabInfo>();
        this.toggleChart = new EventEmitter<void>();
        this.mainGridCssStyles = {};
        this.mainGridColumnList = [];
        this.selectionModel = new Slick.CellSelectionModel<any, Slick.Range[]>();
        this.selectionLineModel = new Slick.CellSelectionModel<any, Slick.Range[]>();
        this.manualEllipsis = {paddingWidth: 9, strings: 2, fontSize: 12.8};
        this.customRowHeight = {fontName: 'Avanti', fontSize: '12.8'};
        this.filterByDate = new FilterByDateRange();
        this.resizeValue = 0;
    }

    @ViewChild('mainWrp')
    protected mainWrp: ElementRef;

    @ViewChild('periodSelectorBtn')
    protected periodSelectorBtn: ElementRef;

    @ViewChild('tabHeaderWrp')
    protected tabHeaderWrp: ElementRef;

    @ViewChild('grid')
    protected gridRef: SlickGridComponent;

    @ViewChild('gridLine')
    protected gridLineRef: SlickGridComponent;

    @ViewChild('headers')
    private headersRef: ElementRef;

    @ViewChild('lines')
    private linesRef: ElementRef;

    @ViewChild('resizeWrp')
    private resizeWrpRef: ElementRef;

    // protected abstract sku: string;
    //
    // protected abstract warehouseCode: string;

    public abstract canSend: boolean;

    public abstract isActive: boolean;

    protected addCustomFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected addSourceFilter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected addTotalsFormatter: (columnSettings: ColumnSettingsBase[], columns: ISlickGridColumn<T>[]) => void;

    protected generateLineColumns: (columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], gridRef: SlickGridComponent) => void;

    protected removeSort: () => void;

    protected resizeChartsLegendsView: boolean;

    protected readonly alertTime = 10000;

    public dataView: Slick.Data.DataView<T>;

    public lineDataView: Slick.Data.DataView<any>;

    public columns: ISlickGridColumn<T>[];

    public lineColumns: ISlickGridColumn<any>[];

    public options: Slick.GridOptions<T>;

    public lineOptions: Slick.GridOptions<T>;

    public showExportError: boolean;

    public showExportErrorTimer: any;

    public showSendError: boolean;

    public showSendErrorTimer: any;

    public showSendSuccess: boolean;

    public showSendSuccessTimer: any;

    public sending: boolean;

    public exporting: boolean;

    protected destroyed: boolean;

    protected popperUpdate: boolean;

    protected showPeriodSelector: boolean;

    protected styleGridCells: boolean;

    public mainGridCssStyles: { [key: string]: Slick.CellCssStylesHash };

    protected mainGridColumnList: string[];

    private subscriptionResizeGrid: ISubscription;

    private subscriptionUpdateChartZoom: ISubscription;

    private subscriptionHideDropdowns: ISubscription;

    private _tab: TabInfoBase<any>;

    public resizeTimer: any;
    private resizeValue: number;
    public heightCoeff: { gr: number, fgr: number };

    @Input()
    protected columnSettings?: ColumnSettingsBase[];

    @Input()
    protected columnLineSettings?: ColumnSettingsBase[];

    @Input()
    public set tab(tab: TabInfoBase<any>){
        this._tab = tab;
        this._tab.customRowHeight = this.helper.readFromLocalStorageJSON(this._tab.id);
        // console.log(this.helper.readFromLocalStorageJSON(this._tab.id), 'this.helper.readFromLocalStorageJSON(this._tab.id)')
    }

    public get tab(): TabInfoBase<any>{
        return this._tab;
    }

    @Input()
    protected chartData: ChartData;

    @Input()
    protected sharedGUID: string;

    @Input()
    public chartType: BGChartType;

    @Input()
    protected isAdmin: boolean;

    // @Output()
    // public updateState: EventEmitter<string>;

    @Output()
    public close: EventEmitter<ITabInfo>;

    @Output()
    protected updateData: EventEmitter<PeriodSelectorModel>;

    @Output()
    protected toggleChart: EventEmitter<void>;

    protected generateColumns: boolean;

    protected helper: ChartHelper;

    public fullWidthTab: boolean;

    protected mainRowsSheetName: string;

    protected treeRowsSheetName: string;

    protected lineRowsSheetName: string;

    protected resizeGrids: boolean;

    protected updateColumns: boolean;

    protected filterByDate: FilterByDateRange;

    public selectionModel: Slick.CellSelectionModel<any, Slick.Range[]>;
    public selectionLineModel: Slick.CellSelectionModel<any, Slick.Range[]>;

    public manualEllipsis: { paddingWidth: number, strings: number, fontSize: number };

    public customRowHeight: { fontName: string, fontSize: string };

    public setCustomRowHeight: boolean;

    public gridPropsName: ModuleTableName;
    public gridPropsNameLine: ModuleTableName;

    @HostListener('window:resize')
    private onResize = (): void => {
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
    }


    ngOnChanges(changes: SimpleChanges): void{
        if (changes['isActive'] && !changes['isActive'].currentValue)
            return;
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
        this.updateColumns = true;
    }

    ngOnInit(): void{
        // this.graphService.onTabsResized.takeWhile(() => !this.destroyed).subscribe(() => this.onTabsResized());
        //# recalculate tab dimensions, resize grids

        this.subscriptionResizeGrid = this.exchangeService.tabsResizeCurrent.subscribe(sharedGUID => this.helper.tryCatch(() =>this.onTabsResized(sharedGUID)));

        //# update zoom for main and compare chart
        this.subscriptionUpdateChartZoom = this.exchangeService.updateChartZoomCurrent.subscribe((r: { type: string, sharedGUID: string, start: Date, end: Date, startFull: boolean, endFull: boolean }) =>
            this.helper.tryCatch(() => {
                if (r && (this.sharedGUID === r.sharedGUID))
                    {
                        // console.log(r.start, r.end, 'tab get zoom');
                        this.filterByDate.setDates({start: r.start, end: r.end, startFull: r.startFull, endFull: r.endFull});
                    }

                }
            )
        );

        this.subscriptionHideDropdowns = this.exchangeService.hideGridDropdownsCurrent.subscribe((GUID: GUID) => this.helper.tryCatch(() => this.onHideDropdown(GUID)));
        this.dataView && (this.dataView.getItemMetadata = this.metadata(this.dataView.getItemMetadata));
    }

    async ngAfterViewInit(): Promise<void>{
        //this.onTabsResized(this.sharedGUID);
        if (this.generateColumns){
            await this.columnsGenerate(this.columns, this.columnSettings, this.gridRef);
            this.columnLineSettings && this.columnLineSettings.length ? await this.columnsGenerate(this.lineColumns, this.columnLineSettings, this.gridLineRef) : null;
        }
        this.helper.wait(() => this.onTabsResized(this.sharedGUID), 1);
        // this.helper.wait(() =>
        //         $(`[data-toggle="tooltip"]`).tooltip({trigger: 'hover', offset: "50px, 0", template: '<div class="tooltip" role="tooltip"><div class="arrow"></div><div class="tooltip-inner"></div></div>'})
        //     , 500);
        if (this.tab.gridFilters && this.tab.gridFilters.length){
            this.restoreFilters();
        }
        if (this.tab.sorting){
            this.restoreSorting();
        }

    }

    ngAfterViewChecked(): void{
        if (this.resizeGrids){
            this.resizeGrids = false;

            if (this.gridRef){
                this.gridRef.resizeGrid();
            }

            if (this.gridLineRef){
                this.gridLineRef.resizeGrid();
            }
        }

        if (this.updateColumns){
            this.updateColumns = false;

            if (this.gridRef){
                this.gridRef.setColumns(this.columns);
            }

            if (this.gridLineRef){
                this.gridLineRef.setColumns(this.lineColumns);
            }
        }
    }

    ngOnDestroy(): void{
        this.destroyed = true;
        this.subscriptionResizeGrid.unsubscribe();
    }

    //# set cell styles to main grid
    protected setMainGridSellStyles = (): void => {
        this.mainGridCssStyles = this.helper.setCellStyles(this.dataView);
    }

    /**
     * Hide dropdown filter in tab
     * @param sharedGUID
     */
    protected onHideDropdown = (sharedGUID: GUID): void => {
        if (sharedGUID && this.sharedGUID !== sharedGUID.GUID){
            return;
        }

        if (this.gridRef){
            this.gridRef.hideDropdownFilters();
        }

        if (this.gridLineRef){
            this.gridLineRef.hideDropdownFilters();
        }
    }

    //# recalculate tab dimensions, resize grids
    protected onTabsResized(sharedGUID: string): void{
        if (this.sharedGUID !== sharedGUID){
            return;
        }

        this.resizeGrids = true;
        // this.updateColumns = true;
        this.popperUpdate = true;
        this.resizeChartsLegendsView = true;

        if (this.tab.viewMode){
            try{
                this.fullWidthTab = false;
                // this.helper.wait(() => this.fullWidthTab = false);
            } catch (e){

            }

            if (this.mainWrp && this.mainWrp.nativeElement && this.tabHeaderWrp && this.tabHeaderWrp.nativeElement){
                this.renderer.setStyle(this.tabHeaderWrp.nativeElement, 'width', 'auto');
                this.helper.wait(() => {
                    let normalWidth = this.mainWrp.nativeElement.parentElement.offsetWidth - 25,
                        headerWidth = this.tabHeaderWrp.nativeElement.offsetWidth;
                    // console.log(normalWidth, headerWidth, 'normalWidth, headerWidth')
                    if (normalWidth < headerWidth){
                        // console.log('normalWidth < headerWidth')
                        this.helper.wait(() => {
                            this.fullWidthTab = true;
                        })
                        // console.log(this.tabHeaderWrp.nativeElement, 'this.tabHeader')
                    } else{
                        this.helper.wait(() => this.fullWidthTab = false);
                    }
                }, 10)
            }
        } else{

        }
    }

    protected removeTab = (tab: ITabInfo): void => {
        this.close.emit(tab);
    }

    //# sens excel to email function
    protected sendToEmail = (makeRequest: (data: T2) => Observable<{ success: boolean }>): void => {
        this.export(true, makeRequest, null);
    }

    //# download excel to email function
    protected download = (makeRequest: (data: T2) => Observable<{ fileContent: Blob, fileName: string }>): void => {
        this.export(false, null, makeRequest);
    }

    //# generate columns for grids in tab
    protected async columnsGenerate(columns: ISlickGridColumn<T>[], columnsSettings: ColumnSettingsBase[], gridRef: SlickGridComponent): Promise<void>{

        let generator = new ColumnsGenerator(this.translateService, this.renderer, this.elementRef);
        //# add custom filters or formatters to grid
        generator.addCustomFormatter = this.addCustomFormatter;
        generator.addSourceFilter = this.addSourceFilter;
        generator.addTotalsFormatter = this.addTotalsFormatter;

        await generator.columnsGenerate(columns, columnsSettings, this.isAdmin);

        if (gridRef){
            gridRef.setColumns(columns);
        } else{
            this.helper.wait(() => {
                if (gridRef)
                    gridRef.setColumns(columns);
            }, 100)
        }

        // console.log(columns, 'columns')
    }

    protected abstract getExportData: () => T2;

    //# save to row data from grid row
    protected saveRow = (row: any, val: any, prop: string, convRow: any): void => {
        if (moment.isMoment(val)){
            convRow[prop] = val.toJSON();
        } else if (moment.isDuration(val)){
            convRow[prop] = moment(val.asMilliseconds()).toJSON();
        } else{
            convRow[prop] = `${val == null ? '' : val}`;
        }
    };

    //# get export data for default table (one grid no tree structure)
    protected getExportDataByDefault = (): { columns: ExportExcelColumn[], mainRows: any[] } => {
        const columns = this.helper.getColumnsForExcelExport(this.gridRef.getColumns());
        const realColumns = this.gridRef.getColumns();
        /*let mainRows = [];

        const length = this.dataView.getLength();
        for (let i = 0; i < length; i++){
            const data: any = this.dataView.getItem(i);
            if (data.hideRow) { continue; }

            const row = {};
            columns.forEach(c => {
                this.saveRow(data, data[c.field], c.field, row);
            });
            mainRows.push(row);
        }

        !mainRows.length ? mainRows = null : null;

        return {columns, mainRows};*/

        const length = this.dataView.getLength();
        let mainRows = [],
            metaData: string[] = [];
            //columnsSpan: string[] = this.columnsSpan ? this.columnsSpan : [],
            //grouping: string[] = groups && groups.length ? groups.map(g => g.getter as string) : [];
        for (let i = 0; i < length; i++){
            const item: any = this.dataView.getItem(i);
            const row = {};
            for (let prop in item){
                let found = prop.match(/[\w\-]+Style/);
                if (found){
                    metaData.push(prop);
                    this.saveRow(item, item[prop], prop, row);
                }
            }

            if (realColumns && realColumns.length){
                realColumns.forEach(c =>
                    c.formatter
                        ? row[c.field] = this.helper.dataItemColumnValueExtractor(item, c)
                        : this.saveRow(item, item[c.field], c.field, row)
                )
            } else {
                columns.forEach(c =>{
                    this.saveRow(item, item[c.field], c.field, row);
                });
            }

            //columnsSpan.length && this.detectSpan && this.detectSpan(row);

            mainRows.push(row);
        }

        !mainRows.length ? mainRows = null : null;

        // console.log(realColumns, mainRows, 'mainRows')

        //metaData = this.helper.checkUniqueByObj(metaData);
        return {columns, mainRows}
    }

    //# export data to file or send file to email
    private export = (toEmail: boolean, emailRequest: (data: T2) => Observable<{ success: boolean }>, downloadRequest: (data: T2) => Observable<{ fileContent: Blob, fileName: string }>): void => {
        if (toEmail)
            this.sending = true;
        else
            this.exporting = true;

        const showError = (): void => {
            if (toEmail){
                this.showExportError = false;
                this.showSendSuccess = false;

                if (this.showSendErrorTimer)
                    clearTimeout(this.showSendErrorTimer);

                this.showSendError = true;
                setTimeout(() => {
                        this.showSendError = false;
                        clearTimeout(this.showSendErrorTimer);
                    },
                    this.alertTime);
                return;
            }

            this.showSendError = false;
            this.showSendSuccess = false;

            if (this.showExportErrorTimer)
                clearTimeout(this.showExportErrorTimer);

            this.showExportError = true;
            setTimeout(() => {
                    this.showExportError = false;
                    clearTimeout(this.showExportErrorTimer);
                },
                this.alertTime);
        };

        if (toEmail){
            emailRequest(this.getExportData()).first().subscribe((data: { success: boolean }) => {
                this.sending = false;

                if (data && data.success){
                    if (this.showSendSuccess)
                        clearTimeout(this.showSendSuccessTimer);

                    this.showExportError = false;
                    this.showSendError = false;
                    this.showSendSuccess = true;
                    setTimeout(() => {
                            this.showSendSuccess = false;
                            clearTimeout(this.showSendSuccessTimer);
                        },
                        this.alertTime);
                    return;
                }

                showError();
            }, () => {
                this.sending = false;
                showError();
            });
            return;
        }

        downloadRequest(this.getExportData()).first().subscribe((data: { fileContent: Blob, fileName: string }) => {
                this.exporting = false;
                if (data && data.fileContent && data.fileContent.size > 0){
                    fileSaver.saveAs(data.fileContent, data.fileName);
                    return;
                }

                showError();
            },
            () => {
                this.exporting = false;
                showError();
            });
    }

    //# toggle period selector visibility, not used now
    protected onTogglePeriodSelector = (): void => {
        this.showPeriodSelector = !this.showPeriodSelector;
    }

    //# hide period selector, emit selected period, not used now
    protected onShowPeriodClick = (dates: PeriodSelectorModel): void => {
        dates ? this.updateData.emit(dates) : null;
        this.showPeriodSelector = false;
    }

    //# hide messages with delay
    public onHideExportErrorClick = (): void => {
        setTimeout(() => this.showExportError = false, 500);
    }

    //# hide messages with delay
    public onHideSendErrorClick = (): void => {
        setTimeout(() => this.showSendError = false, 500);
    }

    //# hide messages with delay
    public onHideSendSuccessClick = (): void => {
        setTimeout(() => this.showSendSuccess = false, 500);
    }

    /**
     * Save applied filters in tab
     * @param data
     */
    public saveFilters = (data: { filteredColumns: string[], filters: OnDropdownFilteredEventArgs<any>[] }): void => {
        this.tab.gridFilters = data.filters.map(i => i);
    }

    /**
     * Restore grid filters in tab
     */
    public restoreFilters = (): void => {
        this.gridRef.addFilters(this.tab.gridFilters.map(item => item.column.field), this.tab.gridFilters);
        // this.gridRef.addFilters(this.tab.gridFilters)
    }

    /**
     * Save sorting for tab
     * @param args
     */
    public saveSorting = (args: { event: Slick.EventData | DOMEvent, data: Slick.OnSortEventArgs<any> }): void => {
        this.tab.sorting = args
            ? {columnId: args.data.sortCols[0].sortCol.id, asc: args.data.sortCols[0].sortAsc}
            : null;
        this.removeSort && this.removeSort();
    }

    /**
     * Restore sorting for tab
    */
    public restoreSorting = (): void => {
        let data = this.dataView.getItems();
        if (data && data.length){

            let columnId = this.tab.sorting.columnId,
                asc = this.tab.sorting.asc;

            this.helper.sortDataInObjArray({field: columnId, columnId: columnId, sortAsc: asc}, data)
            this.dataView.setItems(data);
            this.gridRef.setSortColumn(columnId, asc);
            this.gridRef.invalidate();
        }

    }

    /**
     * Filter data in tab when we change data zoom on chart
     */
    public readonly setFilterZoom = (): void =>{
        try{
            let json: {main: string, reserve: string} = JSON.parse(this.tab.zoomFilterField)
            this.filterByDate.field = json && json.main ? json.main.split(';') : []
            this.filterByDate.reserveField = json && json.reserve ? json.reserve.split(';') : []
        } catch (e){
            this.filterByDate.field = []
            this.filterByDate.reserveField = []
        }

        this.filterByDate.view = this.dataView;
        this.filterByDate.grid = this.gridRef;
        this.gridRef.zoomFilteredColumns = this.filterByDate.field.concat(this.filterByDate.reserveField);
    }

    //# set filter on grid
    public setFilter = (val: boolean): void => {
        if (!val){
            return;
        }

        this.setFilterZoom();

        this.gridRef.filterHelper.endFilterChange = (data: any): CustomFunctionReturn => {
            return this.filterByDate.filter(data);
        };

        this.dataView.setFilter(o => this.gridRef.filterHelper.filter(o));
        this.helper.wait(() => this.tab.customRowHeight && this.setCustomRowHeightState(this.tab.customRowHeight, this.gridRef), 500);
        // console.log(this.tab, 'setFilter')
    }

    //# set filter on grid
    public setFilterLine = (val: boolean): void => {
        if (!val){
            return;
        }
        this.helper.wait(() => {
            // console.log(this.gridLineRef, this.gridLineRef.filterHelper, 'this.gridLineRef.filterHelper')
            this.gridLineRef &&
            this.gridLineRef.filterHelper &&
            this.gridLineRef.filterHelper.filter &&
            this.lineDataView.setFilter(o => this.gridLineRef.filterHelper.filter(o));
            this.tab.customRowHeight && this.setCustomRowHeightState(this.tab.customRowHeight, this.gridLineRef);
            this.onTabsResized(this.sharedGUID);
        }, 100)
    }

    /**
     * add metadata for custom row-height
     * @param old_metadata_provider
     */
    protected metadata = function (old_metadata_provider){
        return function (row){
            let item = this.getItem(row);
            let ret = (old_metadata_provider(row) || {});

            if (item.rowHeight && item.rowHeight > 1){
                ret.cssClasses = (ret.cssClasses || '');
                ret.cssClasses += ` custom-height custom-height-${item.rowHeight}`;
                // ret.attr = (ret.attr || {});
                // ret.attr['height'] = item.rowHeight*25;
            }

            if (item.hideRow){
                ret.cssClasses = (ret.cssClasses || '');
                ret.cssClasses += ` hide-row`;
            }
            return ret;
        }
    };

    /**
     * Set custom row Height in tab
     * @param state -
     * @param grid - grid component
     */
    public readonly setCustomRowHeightState = (state: boolean, grid?: SlickGridComponent): void => {
        // console.log(state, 'setCustomRowHeightState')

        this.setCustomRowHeight = state;
        if (state){
            if (grid){
                grid && grid.setRowHeight(true);
            } else {
                this.gridRef && this.gridRef.setRowHeight(true);
                this.gridLineRef && this.gridLineRef.setRowHeight(true);
            }
        } else {
            if (grid){
                grid && grid.removeEmptyRows(true);
            } else {
                this.gridRef && this.gridRef.removeEmptyRows(true);
                this.gridLineRef && this.gridLineRef.removeEmptyRows(true);
            }

        }

    }

    //# if fresh, we have resizer, if we change height of grids, we update grids
    public readonly onSplitterTabsResized = (): boolean =>{
        return !!this.onTabsResized(this.sharedGUID);
        // if (!this.onTabsResized(this.sharedGUID))
        //     return false;
        //
        // return true;
    }

    //# recalculate grid dimensions when we pull resizer
    public onGridsResizedBy = (size: Size): void =>{
        if (this.resizeTimer)
            clearTimeout(this.resizeTimer);

        this.resizeValue += size.height;

        this.resizeTimer = setTimeout(() =>{
            clearTimeout(this.resizeTimer)
            this.resizeTimer = null;

            let headersHeight = this.headersRef.nativeElement.offsetHeight,
                linesHeight = this.linesRef.nativeElement.offsetHeight;
            const resizeWrpRefHeight = this.resizeWrpRef.nativeElement.offsetHeight,
                diff = resizeWrpRefHeight - (headersHeight + linesHeight + 5);
            // console.log(diff, 'diff')

            if (diff > 1 || diff < -1){
                headersHeight += diff * (headersHeight - 5) / resizeWrpRefHeight;
                linesHeight += diff * (linesHeight - 5) / resizeWrpRefHeight;
            }

            let gr = (headersHeight + this.resizeValue - 5) * 100 / resizeWrpRefHeight,
                fgr = (linesHeight - this.resizeValue) * 100 / resizeWrpRefHeight;

            if (gr * resizeWrpRefHeight / 100 < 100){
                this.heightCoeff.gr = (100 / resizeWrpRefHeight) * 100;
                this.heightCoeff.fgr = ((resizeWrpRefHeight - 100 - 5) / resizeWrpRefHeight) * 100;
            } else if (fgr * resizeWrpRefHeight / 100 < 100){
                this.heightCoeff.fgr = this.heightCoeff.gr = (100 / resizeWrpRefHeight) * 100;
                this.heightCoeff.gr = ((resizeWrpRefHeight - 100 - 5) / resizeWrpRefHeight) * 100;
            } else{
                this.heightCoeff.gr = gr;
                this.heightCoeff.fgr = fgr;
            }

            /*let heightCoeff = (headersHeight - this.resizeValue) / (headersHeight + linesHeight);
            if (heightCoeff < 0)
                heightCoeff = 0;
            else if (heightCoeff > 1)
                heightCoeff = 1;

            localStorage['IaHeightCoeff'] = heightCoeff;
            this.freshTemplateRows = `${heightCoeff}fr min-content ${1 - heightCoeff}fr min-content`;*/

            this.onSplitterTabsResized();

            this.resizeValue = 0;
        }, 100);
    }

}



// WEBPACK FOOTER //
// ./src/app/buffer-graph/models/tab-component-base.ts